// src/resources/Drivers/DriverList.js

import React, { useEffect, useState } from "react";
import {
  Datagrid,
  TextField,
  EditButton,
  useListController,
  ListContextProvider,
  Loading,
  FunctionField,
  useListContext,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/firebase";
import { collection, onSnapshot, doc, serverTimestamp, runTransaction } from "firebase/firestore";
import { sha256Hex, random6DigitCode, randomToken } from "../../utils/crypto";

// Documents tracked in the public upload portal
const DRIVER_DOCS = [
  { fileKeys: ["passportFile", "passportFileBack"], label: "Passport (2 pages)" },
  { fileKeys: ["emiratesIdFile", "emiratesIdFileBack"], label: "Emirates ID (front & back)" },
  { fileKeys: ["licenseFile", "licenseFileBack"], label: "Driving License (front & back)" },
  { fileKeys: ["rtaPermitFile"], label: "RTA Permit" },
  { fileKeys: ["rtaTrainingFile"], label: "RTA Training" },
  { fileKeys: ["healthInsuranceFile"], label: "Health Insurance" },
];

function getMissingDocLabels(driver) {
  if (!driver) return [];
  // "Missing documents" = file not uploaded yet (you can also add expiry checks if you want)
  return DRIVER_DOCS.filter((d) => d.fileKeys.some((key) => !driver[key])).map((d) => d.label);
}

function normalizeWhatsAppNumber(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  let n = v.replace(/[^0-9+]/g, "");
  if (n.startsWith("+")) n = n.slice(1);
  if (n.startsWith("00")) n = n.slice(2);

  // UAE common: 05xxxxxxxx -> 9715xxxxxxx
  if (/^0\d{9,}$/.test(n) && n.startsWith("05")) {
    n = `971${n.slice(1)}`;
  }

  return n.replace(/\D/g, "");
}

async function createDriverDocsInvite({ driver }) {
  const token = randomToken();
  const code = random6DigitCode();
  const codeHash = await sha256Hex(`${token}:${code}`);

  const pointerRef = doc(db, "driverDocActiveInvites", driver.id);
  const inviteRef = doc(db, "driverDocInvites", token);

  // One current link per employee. Creating a new link atomically disables
  // the previous current link and replaces the pointer. Concurrent link
  // generation is safe because Firestore retries this transaction.
  await runTransaction(db, async (transaction) => {
    const pointerSnap = await transaction.get(pointerRef);
    const previousToken = pointerSnap.exists() ? pointerSnap.data()?.token : null;

    if (previousToken && previousToken !== token) {
      transaction.set(
        doc(db, "driverDocInvites", previousToken),
        {
          active: false,
          supersededAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    transaction.set(inviteRef, {
      active: true,
      driverId: driver.id,
      driverName: driver.fullName || "",
      company: driver.company || driver.assignedCarCompany || "",
      codeHash,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    transaction.set(pointerRef, {
      driverId: driver.id,
      token,
      updatedAt: serverTimestamp(),
    });
  });

  const baseUrl = window.location.origin;
  const usesHash = window.location.hash.startsWith("#/");
  const link = usesHash
    ? `${baseUrl}/#/driver-docs?token=${encodeURIComponent(token)}`
    : `${baseUrl}/driver-docs?token=${encodeURIComponent(token)}`;

  return { token, code, codeHash, link };
}

function buildDocsReminderMessage({ driver, link, code, missing }) {
  const name = driver?.fullName || "Employee";
  const company = driver?.company || driver?.assignedCarCompany || "";
  const missingLine = missing?.length ? `Missing: ${missing.join(", ")}` : "Missing: (please check and upload any pending documents)";

  return (
    `Employee Documents Upload\n` +
    `${company ? `Company: ${company}\n` : ""}` +
    `Name: ${name}\n` +
    `${missingLine}\n\n` +
    `Please open the link below, upload clear photos/PDFs, then confirm expiry dates and submit to admin.\n\n` +
    `Link: ${link}\n` +
    `Secret Code: ${code}\n\n` +
    `Steps:\n1) Open link\n2) Enter code\n3) Upload docs\n4) Submit`
  );
}

const ExpiryCell = ({ record, source, fileField, fileFields }) => {
  if (!record) return null;

  const expiry = record[source];
  const resolvedFileFields = fileFields || (fileField ? [{ key: fileField, label: "View" }] : []);
  let color = "#666";
  let dateLabel = "-";

  if (expiry) {
    const expiryDate = expiry.seconds
      ? new Date(expiry.seconds * 1000)
      : new Date(expiry);
    const diffDays = (expiryDate - new Date()) / (1000 * 60 * 60 * 24);
    dateLabel = expiryDate.toLocaleDateString("en-GB");
    if (diffDays < 0) color = "red";
    else if (diffDays <= 30) color = "#e69500";
    else color = "green";
  }

  return (
    <div style={{ textAlign: "center" }}>
      <span style={{ color, fontWeight: 600 }}>{dateLabel}</span>
      {resolvedFileFields.map(({ key, label }) => {
        const fileUrl = record[key];
        if (!fileUrl) return null;
        return (
          <a
            key={key}
            href={fileUrl?.startsWith("http") ? fileUrl : `https://${fileUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "block",
              fontSize: "12px",
              color: "#1976d2",
              textDecoration: "none",
              marginTop: 2,
            }}
          >
            {label}
          </a>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------
// 🔗 Generate a secure upload link + secret code for this employee
// - creates: driverDocInvites/{token}
// - DOES NOT modify the driver document (so no impact on car sheets)
// ------------------------------------------------------------
const DocsLinkButton = ({ record }) => {
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!record?.id) return;

    try {
      setBusy(true);
      const { link, code } = await createDriverDocsInvite({ driver: record });

      const message = `Employee Documents Upload
      Please open the link below and upload clear photos/PDFs of your documents, then select the expiry dates.
This helps us renew your documents on time and avoid fines or delays.

Link: ${link}
Secret Code: ${code}
Instructions:
1) Open the link
2) Enter the secret code
3) Upload the required documents
4) Select expiry dates and press Submit

Note: Please do not share this code publicly.`;


      try {
        await navigator.clipboard.writeText(message);
        alert("✅ Link + code copied to clipboard. Paste it to the employee.");
      } catch {
        // fallback
        prompt("Copy this link + code and send to employee:", message);
      }
    } catch (err) {
      console.error(err);
      alert("❌ Failed to generate link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handle}
      disabled={busy}
      style={{
        padding: "6px 10px",
        borderRadius: 8,
        border: "1px solid #d1d5db",
        background: busy ? "#e5e7eb" : "#111827",
        color: busy ? "#6b7280" : "#fff",
        fontWeight: 800,
        cursor: busy ? "not-allowed" : "pointer",
      }}
    >
      {busy ? "Generating…" : "Docs Link"}
    </button>
  );
};

// ------------------------------------------------------------
// 📲 WhatsApp reminder (per employee)
// Note: This opens WhatsApp with a pre-filled message.
// It cannot "auto-send" without WhatsApp Business API.
// ------------------------------------------------------------
const WhatsAppDocsReminderButton = ({ record }) => {
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!record?.id) return;

    const phone = normalizeWhatsAppNumber(record.phone);
    if (!phone) {
      alert("❌ This employee has no phone number.");
      return;
    }

    try {
      setBusy(true);
      const missing = getMissingDocLabels(record);
      const { link, code } = await createDriverDocsInvite({ driver: record });
      const message = buildDocsReminderMessage({ driver: record, link, code, missing });

      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to prepare WhatsApp reminder.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handle}
      disabled={busy}
      title="Open WhatsApp message with a unique upload link + code"
      style={{
        padding: "6px 10px",
        borderRadius: 8,
        border: "1px solid #d1d5db",
        background: busy ? "#e5e7eb" : "#10b981",
        color: busy ? "#6b7280" : "#fff",
        fontWeight: 900,
        cursor: busy ? "not-allowed" : "pointer",
        marginLeft: 6,
      }}
    >
      {busy ? "Preparing…" : "WhatsApp"}
    </button>
  );
};

// ------------------------------------------------------------
// 📋 Bulk reminder (admin helper): generate invites for all employees
// missing docs and COPY a list (or download CSV) for WhatsApp sending.
// True bulk sending requires WhatsApp Business API.
// ------------------------------------------------------------
const BulkDocsReminderTools = ({ rows }) => {
  const [busy, setBusy] = useState(false);

  const buildRows = async () => {
    const out = [];
    for (const r of rows || []) {
      const missing = getMissingDocLabels(r);
      if (!missing.length) continue;
      const phone = normalizeWhatsAppNumber(r.phone);
      if (!phone) continue;
      const { link, code } = await createDriverDocsInvite({ driver: r });
      const message = buildDocsReminderMessage({ driver: r, link, code, missing });
      out.push({ fullName: r.fullName || "", phone, message, missing: missing.join("; ") });
    }
    return out;
  };

  const copyAll = async () => {
    try {
      setBusy(true);
      const out = await buildRows();
      if (!out.length) {
        alert("✅ No missing documents found for the current list.");
        return;
      }
      const text = out
        .map((x, i) => `#${i + 1}\nName: ${x.fullName}\nPhone: ${x.phone}\n${x.message}\n---`)
        .join("\n\n");
      await navigator.clipboard.writeText(text);
      alert(`✅ Copied ${out.length} WhatsApp reminder messages to clipboard.`);
    } catch (e) {
      console.error(e);
      alert("❌ Failed to copy messages.");
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async () => {
    try {
      setBusy(true);
      const out = await buildRows();
      if (!out.length) {
        alert("✅ No missing documents found for the current list.");
        return;
      }
      const esc = (s) => `"${String(s || "").replace(/"/g, '""')}"`;
      const header = ["fullName", "phone", "missing", "message"].join(",");
      const lines = out.map((r) => [esc(r.fullName), esc(r.phone), esc(r.missing), esc(r.message)].join(","));
      const csv = [header, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `employee_doc_reminders_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("❌ Failed to download CSV.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
      <button
        onClick={copyAll}
        disabled={busy}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #d1d5db",
          background: busy ? "#e5e7eb" : "#111827",
          color: busy ? "#6b7280" : "#fff",
          fontWeight: 900,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        {busy ? "Working…" : "Copy WhatsApp Reminders (Missing Docs)"}
      </button>

      <button
        onClick={downloadCsv}
        disabled={busy}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #d1d5db",
          background: busy ? "#e5e7eb" : "#f3f4f6",
          color: "#111827",
          fontWeight: 900,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        {busy ? "Working…" : "Download CSV Reminders"}
      </button>

      <div style={{ alignSelf: "center", fontSize: 12, color: "#6b7280" }}>
        Bulk auto-send to all is only possible via WhatsApp Business API (Meta/Twilio). This tool prepares messages.
      </div>
    </div>
  );
};

// ✅ Serial number field (matches current sorting/filtering)
const SerialField = ({ record }) => {
  const { ids } = useListContext();
  if (!record) return null;
  const idx = ids?.indexOf(record.id);
  return <span>{idx >= 0 ? idx + 1 : ""}</span>;
};



export const DriverList = () => {
  const controller = useListController({ resource: "drivers" });
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("All");
  const getTodayLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const [reportDate, setReportDate] = useState(() => getTodayLocal());
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const role = localStorage.getItem("role")?.toLowerCase() || "";
  const canCreate = ["admin", "md"].includes(role);
  const canEdit = ["admin", "md"].includes(role);
  const isDocsLinkUser = role === "docs_link";
  const isOperationUser = role === "operation";

  // Load companies
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs.map((d) => d.data().name).sort();
      setCompanies(list);
    });
    return () => unsub();
  }, []);

  if (!controller || controller.isLoading) return <Loading />;

  const allData = Array.isArray(controller.data)
    ? controller.data
    : Object.values(controller.data || {});

  const reportAsOf = new Date(reportDate + "T23:59:59.999");

  const filteredCompany =
    selectedCompany === "All"
      ? allData
      : allData.filter((d) => d.company === selectedCompany);

      const toDateSafe = (v) => {
  if (!v) return null;
  if (v?.seconds) return new Date(v.seconds * 1000); // Firestore timestamp
  if (v instanceof Date) return v;
  if (typeof v === "string") {
    // supports "YYYY-MM-DD" and "DD/MM/YYYY"
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(v + "T00:00:00");
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
      const [dd, mm, yyyy] = v.split("/");
      return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    }
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const getDriverStartDate = (d) => {
  const created = toDateSafe(d.createdAt);
  const assignedStart = toDateSafe(d.assignedCarStartDate);
  if (created && assignedStart) return created < assignedStart ? created : assignedStart;
  return assignedStart || created || null;
};

// Status shown for a historical report date, not the employee's status today.
const getDriverStatusAsOf = (d, asOf) => {
  const currentStatus = String(d.status || "Active").trim();
  if (currentStatus.toLowerCase() !== "cancelled") {
    return currentStatus || "Active";
  }

  const cancelledAt = toDateSafe(d.cancelledAt);

  // A cancelled employee without a cancellation date cannot safely be
  // treated as historically active, so keep them cancelled.
  if (!cancelledAt) return "Cancelled";

  // If cancellation happened after the selected report date, the employee
  // was still active on that historical date.
  return cancelledAt > asOf ? "Active" : "Cancelled";
};

// ✅ History + cancellation logic (as-of report date)
const filteredData = filteredCompany.filter((d) => {
  // Use real start date (assignedCarStartDate OR createdAt).
  const start = getDriverStartDate(d);
  if (start && start > reportAsOf) return false;

  const statusAsOf = getDriverStatusAsOf(d, reportAsOf).toLowerCase();

  if (statusAsOf !== "cancelled") return true;

  return includeCancelled;
});

const filteredIds = filteredData.map((d) => d.id);


  return (
    <ListContextProvider value={{ ...controller, data: filteredData, ids: filteredIds }}>
      <div style={{ padding: 20 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2 style={{ margin: 0 }}>Employees</h2>
          {canCreate && (
            <button
              onClick={() => navigate("/drivers/create")}
              style={{
                background: "#1976d2",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              ➕ Add Employee
            </button>
          )}
        </div>

        {/* Expiry Legend */}
        <div style={{ display: "flex", gap: "20px", marginBottom: "10px" }}>
          <strong>Expiry Legend:</strong>
          <span style={{ color: "green" }}>🟩 Valid</span>
          <span style={{ color: "#e69500" }}>🟧 Expiring ≤30 days</span>
          <span style={{ color: "red" }}>🟥 Expired</span>
        </div>

        {/* Company dropdown */}
        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          style={{
            marginBottom: "15px",
            padding: "6px 10px",
            border: "1px solid #ccc",
            borderRadius: "6px",
          }}
        >
          <option value="All">All Companies</option>
          {companies.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        {/* Report As Of + Include Cancelled */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 15 }}>
          <strong>Report As Of:</strong>
          <input
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            style={{
              padding: "6px 10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
            }}
          />

          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(e) => setIncludeCancelled(e.target.checked)}
            />
            Include cancelled employees
          </label>
        </div>

        {/* Bulk docs reminder tools (current filtered list) */}
        {!isOperationUser && <BulkDocsReminderTools rows={filteredData} />}


        {/* DATAGRID */}
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          sx={{
            "& .RaDatagrid-row:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <FunctionField label="#" render={(record) => <SerialField record={record} />} />
          <TextField source="fullName" label="Full Name" />
          <TextField source="phone" label="Phone" />

          <FunctionField
            label="Passport"
            render={(record) => (
              <ExpiryCell
                record={record}
                source="passportExpiry"
                fileFields={[
                  { key: "passportFile", label: "Page 1" },
                  { key: "passportFileBack", label: "Page 2" },
                ]}
              />
            )}
          />

          <FunctionField
            label="EID"
            render={(record) => (
              <ExpiryCell
                record={record}
                source="emiratesIdExpiry"
                fileFields={[
                  { key: "emiratesIdFile", label: "Front" },
                  { key: "emiratesIdFileBack", label: "Back" },
                ]}
              />
            )}
          />

          <FunctionField
            label="License"
            render={(record) => (
              <ExpiryCell
                record={record}
                source="licenseExpiry"
                fileFields={[
                  { key: "licenseFile", label: "Front" },
                  { key: "licenseFileBack", label: "Back" },
                ]}
              />
            )}
          />

          <FunctionField
            label="RTA Permit"
            render={(record) => (
              <ExpiryCell
                record={record}
                source="rtaPermitExpiry"
                fileField="rtaPermitFile"
              />
            )}
          />

          <FunctionField
            label="H. Insurance"
            render={(record) => (
              <ExpiryCell
                record={record}
                source="healthInsuranceExpiry"
                fileField="healthInsuranceFile"
              />
            )}
          />

          {/* Assigned Car (read-only) */}
          <FunctionField
            label="Assigned Car"
            render={(record) =>
              record.assignedCarNumber ? (
                <span>
                  {record.assignedCarNumber} ({record.assignedCarType})
                </span>
              ) : (
                <span style={{ color: "#999" }}>No Car</span>
              )
            }
          />

          <FunctionField
            label="Status As Of"
            render={(record) => {
              const statusAsOf = getDriverStatusAsOf(record, reportAsOf);
              const isCancelled = statusAsOf.toLowerCase() === "cancelled";

              return (
                <span
                  style={{
                    color: isCancelled ? "#d32f2f" : "#2e7d32",
                    fontWeight: 600,
                  }}
                >
                  {statusAsOf}
                </span>
              );
            }}
          />

          {!isOperationUser && (
            <FunctionField
              label="Docs"
              render={(record) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <DocsLinkButton record={record} />
                  <WhatsAppDocsReminderButton record={record} />
                </div>
              )}
            />
          )}

          {canEdit ? <EditButton /> : null}
        </Datagrid>
      </div>
    </ListContextProvider>
  );
};
